import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useUrlState } from '@/lib/useUrlState';
import { ApprovalsPage } from '@/pages/leads/ApprovalsPage';
import { CampaignsPage } from '@/pages/leads/CampaignsPage';
import { LeadListsPage } from '@/pages/leads/LeadListsPage';
import { LeadsPage } from '@/pages/leads/LeadsPage';
import { ImportPage } from '@/pages/ImportPage';
import { BrettTab } from './BrettTab';

/**
 * Akquise — eine Seite, fünf Reiter.
 *
 * Vorher waren das sechs Menüpunkte plus Import; jetzt ist es der Weg eines
 * Interessenten: Leads sammeln, in Listen ordnen, per Kampagne ansprechen,
 * Nachfassen freigeben — und das Brett, das bei der ersten positiven Antwort
 * beginnt und bei der Zusage endet. Dort sitzt die Übernahme, der einzige
 * Übergang in die Projektarbeit.
 *
 * Der Reiter steht in der URL (`?reiter=`, Vorgabe `brett`), damit Tiefenlinks
 * wie `/akquise?reiter=freigaben` — und der Zurück-Knopf aus einer Akte —
 * auf dem richtigen Reiter landen. Beim Wechsel fällt alles weg, was nur zu
 * einem anderen Reiter gehörte (`liste`, `kampagne`, `filter` …).
 */
const REITER = [
  { key: 'brett', label: 'Brett' },
  { key: 'leads', label: 'Leads' },
  { key: 'listen', label: 'Lead-Listen' },
  { key: 'kampagnen', label: 'Kampagnen' },
  { key: 'freigaben', label: 'Freigaben' },
] as const;

export type AkquiseReiter = (typeof REITER)[number]['key'];

const SCHLUESSEL = REITER.map((r) => r.key);

export function AkquisePage() {
  const [reiter] = useUrlState<AkquiseReiter>('reiter', 'brett', SCHLUESSEL);
  const [, setSuchparameter] = useSearchParams();

  const wechseln = useCallback(
    (neu: AkquiseReiter) => {
      setSuchparameter(neu === 'brett' ? {} : { reiter: neu }, { replace: true });
    },
    [setSuchparameter],
  );

  return (
    <div>
      <div className="flex gap-1 border-b border-border" role="tablist" aria-label="Akquise">
        {REITER.map((r) => (
          <button
            key={r.key}
            role="tab"
            aria-selected={reiter === r.key}
            onClick={() => wechseln(r.key)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              reiter === r.key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/*
        Die 24 px Abstand sind Absicht: die Kopfzeilen der Leads-Seiten
        (`PageHeader`) heben das Seitenpolster mit `-mt-6` wieder auf, damit sie
        oben kleben. Ohne diesen Abstand rutschten sie über die Reiterleiste.
      */}
      <div className="pt-6" role="tabpanel">
        {reiter === 'brett' && <BrettTab />}
        {reiter === 'leads' && <LeadsPage />}
        {reiter === 'listen' && <ListenTab />}
        {reiter === 'kampagnen' && <CampaignsPage />}
        {reiter === 'freigaben' && <ApprovalsPage />}
      </div>
    </div>
  );
}

/**
 * Lead-Listen mit dem CSV-Import als Knopf daneben — kein eigener Reiter: der
 * Import füllt genau diese Listen. `?import=1` zeigt den Import an Stelle der
 * Listen, damit „Zurück“ und Lesezeichen funktionieren.
 */
function ListenTab() {
  const [importOffen, setImportOffen] = useUrlState<'0' | '1'>('import', '0', ['0', '1']);

  if (importOffen === '1') {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setImportOffen('0')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Lead-Listen
        </Button>
        <ImportPage />
      </div>
    );
  }

  return <LeadListsPage onImport={() => setImportOffen('1')} />;
}
